'use client';

/**
 * The search box in the centre of the navbar (components/nav/SiteNav.tsx).
 *
 *   on Camps     Search TrueMarch: typeahead from `/api/search/suggest` —
 *                Camps, posts, comments, people, games and news, mixed, each
 *                with its picture and its kind (docs/TRUEMARCH_SEARCH.md §6)
 *   elsewhere    `everything`: the same, in the site's own words.
 *
 * Enter opens the results page (/camps/search?q=…). Arrow keys move through the
 * suggestions and Enter opens the one marked; Escape closes the list. If the
 * suggest endpoint does not answer, the old lookup (Camps by name, games on the
 * shelf) stands in, so the box never goes dead.
 */

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { listCamps } from '@/lib/campsApi';
import { GAMES, type Game } from '@/lib/games';
import { suggest, searchHref, type SearchHit, type HitKind } from '@/lib/searchApi';
import { HitIcon, HitLink, Marked, SearchGlyph } from '../search/bits';
import { NAV_SEARCH } from '../nav/styles';

const ROW = 'flex min-h-[48px] items-center gap-3 px-3.5 no-underline hover:bg-ink-100 dark:hover:bg-white/[0.06]';
/** The kind word on the right of a row; a reply reads as a comment. */
const KIND: Record<HitKind, string> = { camp: 'Camp', post: 'Post', comment: 'Comment', reply: 'Comment', person: 'Person', game: 'Game', news: 'News' };
const DEBOUNCE_MS = 180;

/** Every place the query appears in `text`, for bolding. */
function marksOf(text: string, q: string): [number, number][] {
  const n = q.trim().toLowerCase();
  if (!n) return [];
  const out: [number, number][] = [];
  const t = text.toLowerCase();
  for (let i = t.indexOf(n); i >= 0 && out.length < 4; i = t.indexOf(n, i + n.length)) out.push([i, i + n.length]);
  return out;
}

const gameHit = (g: Game): SearchHit => ({
  id: g.id, kind: 'game', url: `/games/${g.id}`, title: g.name, snippet: { text: g.blurb, marks: [] },
  crumbs: ['TrueMarch', 'Games'], when: null, chips: [], image: `/games/covers/${g.id}-1.webp`,
});

/** The old lookup, as suggestions: Camps by name (+ games on the shelf when `everything`). */
async function fallback(s: string, games: Game[], everything: boolean): Promise<SearchHit[]> {
  const r = await listCamps('popular', s.replace(/^c\//i, '')).catch(() => ({ camps: [] }));
  const camps: SearchHit[] = r.camps.slice(0, everything ? 4 : 6).map((c) => ({
    id: c.id, kind: 'camp', url: `/c/${c.slug}`, title: `c/${c.name}`,
    snippet: { text: `${c.memberCount.toLocaleString('en-US')} members · ${c.title}`, marks: [] },
    crumbs: ['TrueMarch', 'Camps'], when: null, chips: [], camp: { slug: c.slug, name: c.name, art: c.art, member: c.myStatus === 'member', type: c.type },
  }));
  return [...camps, ...(everything ? games.map(gameHit) : [])];
}

/** The second line of a suggestion: where it lives and a little of what it says. */
function subline(h: SearchHit): string {
  const text = h.snippet?.text ?? '';
  if (h.kind === 'camp') return text;
  if (h.kind === 'person') return h.stats?.kharma != null ? `${h.stats.kharma.toLocaleString('en-US')} KHARMA${text ? ` · ${text}` : ''}` : text;
  if (h.kind === 'game' || h.kind === 'news') return text;
  const where = h.camp ? `c/${h.camp.name}` : h.crumbs?.[1] ?? '';
  return [where, text].filter(Boolean).join(' · ');
}

export default function CampSearch({ everything = false }: { everything?: boolean }) {
  const router = useRouter();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [hitsFor, setHitsFor] = useState('');
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(-1);
  const wrap = useRef<HTMLDivElement>(null);

  const s = q.trim();
  const needle = s.replace(/^c\//i, '').toLowerCase();
  const games: Game[] = everything && needle
    ? GAMES.filter((g) => g.name.toLowerCase().includes(needle) || g.blurb.toLowerCase().includes(needle)).slice(0, 4)
    : [];
  const gameKey = games.map((g) => g.id).join(',');

  useEffect(() => {
    if (!s) { setHits(null); setHitsFor(''); return; }
    const ac = new AbortController();
    const t = setTimeout(() => {
      suggest(s, ac.signal)
        .catch((e) => { if ((e as Error).name === 'AbortError') throw e; return fallback(s, games, everything); })
        .then((r) => { if (!ac.signal.aborted) { setHits(r); setHitsFor(s); } })
        .catch(() => undefined);
    }, DEBOUNCE_MS);
    return () => { ac.abort(); clearTimeout(t); };
  }, [s, everything, gameKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setAt(-1); }, [q]);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', down);
    return () => document.removeEventListener('mousedown', down);
  }, [open]);

  const items: SearchHit[] = hits ?? [];
  const pick = (href: string) => { setOpen(false); setQ(''); router.push(href); };
  const go = () => {
    setOpen(false); router.push(searchHref({ q: s }));
  };
  const openItem = (h: SearchHit) => {
    if (/^https?:\/\//i.test(h.url)) { setOpen(false); window.open(h.url, '_blank', 'noopener'); return; }
    pick(h.url);
  };
  const mark = (i: number) => (at === i ? ' bg-ink-100 dark:bg-white/[0.06]' : '');
  const shown = open && !!s;
  const stale = hitsFor !== s;

  return (
    <div ref={wrap} className="relative w-full min-w-[64px]">
      <form role="search" onSubmit={(e) => { e.preventDefault(); if (at >= 0 && items[at]) openItem(items[at]); else if (s) go(); }}>
        <label className="relative block">
          <span className="sr-only">{everything ? 'Search TrueMarch World' : 'Search TrueMarch'}</span>
          <svg viewBox="0 0 24 24" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400 dark:text-white/45" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><circle cx="11" cy="11" r="6.5" /><path d="M20 20l-4-4" /></svg>
          <input type="search" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
                 role="combobox" aria-expanded={shown} aria-autocomplete="list" aria-controls="tm-suggest"
                 aria-activedescendant={shown && at >= 0 ? `tm-suggest-${at}` : undefined}
                 onKeyDown={(e) => {
                   if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setAt(-1); (e.target as HTMLInputElement).blur(); } // keeps the text (a search input clears itself on Escape)
                   if (e.key === 'ArrowDown' && items.length) { e.preventDefault(); setOpen(true); setAt((i) => (i + 1) % items.length); }
                   if (e.key === 'ArrowUp' && items.length) { e.preventDefault(); setAt((i) => (i <= 0 ? items.length - 1 : i - 1)); }
                 }}
                 placeholder={everything ? 'Search TrueMarch World' : 'Search TrueMarch'}
                 className={NAV_SEARCH} />
        </label>
      </form>
      {shown && (
        <div id="tm-suggest" role="listbox" aria-label="Suggestions"
             className="absolute left-0 right-0 top-full z-[120] mt-1.5 max-h-[70vh] overflow-y-auto max-sm:fixed max-sm:inset-x-2 max-sm:top-[calc(var(--nav-offset,56px)+2px)] rounded-2xl border border-ink-200 bg-white py-1 text-ink-900 shadow-xl dark:border-white/10 dark:bg-ink-900 dark:text-ink-50">
          <div className={stale && hits ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            {items.map((h, i) => {
              const sub = subline(h);
              return (
                <HitLink key={`${h.kind}:${h.id}`} hit={h} onClick={() => { setOpen(false); setQ(''); }} className={ROW + mark(i)}>
                  <span id={`tm-suggest-${i}`} role="option" aria-selected={at === i} className="contents">
                    <HitIcon hit={h} size={30} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13.5px] font-medium"><Marked text={h.title} marks={marksOf(h.title, s.replace(/^(?:@|[cu]\/)/i, ''))} /></span>
                      {sub && <span className="block truncate text-[12px] text-ink-500 dark:text-ink-400">{sub}</span>}
                    </span>
                    <span className="shrink-0 rounded-full bg-ink-100 px-2 py-px text-[10.5px] font-semibold uppercase tracking-wide text-ink-500 dark:bg-white/[0.07] dark:text-ink-400">{KIND[h.kind] ?? h.kind}</span>
                  </span>
                </HitLink>
              );
            })}
            {hits && !stale && !items.length && <p className="px-3.5 py-3 text-[13px] text-ink-500">No quick matches. Press Enter to search everything.</p>}
          </div>
          <button type="button" onClick={go}
                  className={'flex min-h-[44px] w-full items-center gap-2.5 px-3.5 text-left text-[13px] font-semibold text-accent-700 hover:bg-ink-100 dark:text-accent-300 dark:hover:bg-white/[0.06]' + (items.length || (hits && !stale) ? ' border-t border-ink-100 dark:border-white/10' : '')}>
            <SearchGlyph className="h-4 w-4" />
            <span className="min-w-0 truncate">Search TrueMarch for “{s}”</span>
          </button>
        </div>
      )}
    </div>
  );
}
